export const FIX_CACHE_KEY = "@sayagie/last-location-fix";
export const FIRST_FIX_GRACE_MS = 5000;
export const FIX_JITTER_METERS = 300;

const EARTH_RADIUS_METERS = 6371000;
const toRadians = (degrees) => (degrees * Math.PI) / 180;

export const metersBetween = (from, to) => {
  if (!from || !to) return Infinity;

  const dLat = toRadians(to.latitude - from.latitude);
  const dLng = toRadians(to.longitude - from.longitude);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a));
};

const HIGH_ACCURACY_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 120000,
};

const COARSE_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 8000,
  maximumAge: 600000,
};

let memoryFix = null;
let inFlight = null;

export const readFix = () => {
  if (memoryFix) return memoryFix;

  try {
    const raw = window.localStorage.getItem(FIX_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.coordinates) return null;

    memoryFix = parsed;

    return parsed;
  } catch {
    return null;
  }
};

export const writeFix = (fix) => {
  memoryFix = fix;

  try {
    window.localStorage.setItem(FIX_CACHE_KEY, JSON.stringify(fix));
  } catch {
    /* private mode, blocked storage */
  }
};

const getPosition = (options) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("geolocation-unsupported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      reject,
      options
    );
  });

export const requestFix = () => {
  if (inFlight) return inFlight;

  inFlight = getPosition(HIGH_ACCURACY_OPTIONS)
    .catch(() => getPosition(COARSE_OPTIONS))
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

export const readPermission = async () => {
  if (!navigator.permissions?.query) return "unknown";

  try {
    const status = await navigator.permissions.query({ name: "geolocation" });

    return status.state;
  } catch {
    return "unknown";
  }
};

export const watchPermission = (onChange) => {
  if (!navigator.permissions?.query) return () => {};

  let status = null;
  let cancelled = false;

  const handle = () => onChange(status.state);

  navigator.permissions
    .query({ name: "geolocation" })
    .then((result) => {
      if (cancelled) return;

      status = result;
      status.addEventListener("change", handle);
    })
    .catch(() => {});

  return () => {
    cancelled = true;
    status?.removeEventListener("change", handle);
  };
};

export const isPermissionDenied = (error) => error?.code === 1;

const LABEL_PRIORITY = [
  "locality",
  "postal_town",
  "administrative_area_level_2",
  "administrative_area_level_1",
];

const componentOf = (results, type) => {
  for (const result of results) {
    const match = (result.address_components || []).find((component) =>
      component.types.includes(type)
    );

    if (match) return match.long_name;
  }

  return null;
};

export const coordinateLabel = ({ latitude, longitude }) =>
  `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

export const reverseGeocode = ({ latitude, longitude }) =>
  new Promise((resolve) => {
    const maps = window.google?.maps;

    if (!maps?.Geocoder) {
      resolve(null);
      return;
    }

    new maps.Geocoder().geocode(
      { location: { lat: latitude, lng: longitude } },
      (results, status) => {
        if (status !== "OK" || !results?.length) {
          resolve(null);
          return;
        }

        const city = LABEL_PRIORITY.map((type) =>
          componentOf(results, type)
        ).find(Boolean);

        const country = componentOf(results, "country");

        resolve({
          address: results[0].formatted_address,
          shortLabel: [city, country].filter(Boolean).join(", ") || null,
        });
      }
    );
  });
